import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  NgForm,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { sectionAnimations } from '@platform/helpers/animations.helper';
import { isEqual, noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';

const CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => EMMAMultiInputComponent),
  multi: true,
};

const VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => EMMAMultiInputComponent),
  multi: true,
};

@Component({
    selector: 'emma-multi-input',
    templateUrl: './emma-multi-input.component.html',
    providers: [
        CONTROL_VALUE_ACCESSOR,
        VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAMultiInputComponent,
        },
    ],
    animations: [...sectionAnimations],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    standalone: false
})
export class EMMAMultiInputComponent
  extends EMMAFormElementComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: Array<string | number>) => void = noop;

  @Input() values: Array<string | number> = [];
  inputs: Array<{ value: string | number }> = [];
  areValid: Array<boolean> = [];
  @Output() valuesChange = new EventEmitter<Array<string | number>>();

  @Input() allowRemove = true;
  @Input() maxLength = Infinity;
  @Input() minLength = 0;
  @Input() type = 'text';
  @Input() createButton = $localize`Añadir entrada`;
  @Input() override placeholder = '';

  onChangeInput = (index: number, value: string | number): void => {
    this.inputs[index].value = value;
    this.values[index] = value;
    this.emitChange();
  };
  onEnterInput = (index: number): void => {
    if (index === this.inputs.length - 1 && this.inputs[index].value) {
      this.onAddButton();
    }
  };

  onAddButton(): void {
    if (!this.values) {
      this.values = [''];
      this.inputs = [{ value: '' }];
      this.areValid = [];
    } else {
      this.values.push('');
      this.inputs.push({ value: '' });
    }
    this.emitChange();
  }
  onRemove(index: number): void {
    this.values.splice(index, 1);
    this.inputs.splice(index, 1);
    this.areValid.splice(index, 1);
    this.emitChange();
  }
  onBlur(): void {
    this.onTouchedCallback();
  }

  emitChange(): void {
    this.valuesChange.emit(this.values);
    this.onChangeCallback(this.values);
  }

  private syncWithValues(): void {
    this.inputs = this.values.map((value) => ({ value }));
  }

  // private syncWithInputs(): void {
  //   for (let i = 0; i < this.inputs.length; i++) {
  //     if (!this.inputs[i]) {
  //       this.values.splice(i, 1);
  //     } else if (this.values[i] !== this.inputs[i].value) {
  //       this.values[i] = this.inputs[i].value;
  //     }
  //   }
  //   this.emitChange();
  // }

  // BEGIN From ControlValueAccessor interface
  writeValue(values: Array<string | number> | null | undefined): void {
    if (values && !isEqual(this.values, values)) {
      this.values = values;
      this.syncWithValues();
    }
  }
  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }
  // END From ControlValueAccessor interface

  // From Validator interface
  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];
    if (this.maxLength < Infinity) {
      validators.push(Validators.maxLength(this.maxLength));
    }
    if (this.minLength) {
      validators.push(Validators.minLength(this.minLength));
    } else if (this.required) {
      validators.push(Validators.minLength(1));
    }
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      return validator ? validator(control) : null;
    }
    return null;
  };
}
